import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Package, Wrench, Users, Building2, MessageSquare, LogOut, User, ChevronDown, Settings, Bell, Gift, FolderKanban, MapPin, Menu, X } from 'lucide-react';
import NotificationDropdown from './NotificationDropdown';
import SmartImg from './SmartImg';
import { initials, avatarColor } from '../utils/initials';
import { logo as LOGO_SRC } from '../assets';


const navLinks = [
  { path: '/feed', icon: Home, label: 'Discover' },
  { path: '/giveaways', icon: Gift, label: 'Giveaways' },
  { path: '/friends', icon: Users, label: 'Friends' },
  { path: '/communities', icon: Building2, label: 'Communities' },
  { path: '/messages', icon: MessageSquare, label: 'Messages' },
];

// Bottom-tab quick-access on mobile (5 hottest destinations)
const mobileNavLinks = [
  { path: '/feed',        icon: Home,          label: 'Discover' },
  { path: '/giveaways',   icon: Gift,          label: 'Giveaways' },
  { path: '/friends',     icon: Users,         label: 'Friends' },
  { path: '/communities', icon: Building2,     label: 'Communi.' },
  { path: '/messages',    icon: MessageSquare, label: 'Messages' },
];

// Rendered inside both the desktop dropdown and the mobile drawer so we
// only have one source of truth for the "My X" links.
const drawerLinks = [
  { path: '/profile',       icon: User,        label: 'My Profile',   testId: 'nav-profile', useUserId: true },
  { path: '/projects',      icon: FolderKanban,label: 'My Projects',  testId: 'nav-my-projects' },
  { path: '/items',         icon: Package,     label: 'My Library',   testId: 'nav-my-library' },
  { path: '/my-giveaways',  icon: Gift,        label: 'My Giveaways', testId: 'nav-my-giveaways' },
  { path: '/settings',      icon: Settings,    label: 'Settings',     testId: 'nav-settings' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);        // desktop avatar dropdown
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // mobile hamburger drawer

  const handleLogout = () => { logout(); navigate('/'); };
  const isActive = (path) => location.pathname.startsWith(path);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const resolveHref = (link) => link.useUserId ? `${link.path}/${user?.id}` : link.path;
  const uc = avatarColor(user);

  return (
    <>
      {/* ── Desktop Top Nav ─────────────────────────────────────────────── */}
      <nav className="hidden md:flex sticky top-0 left-0 right-0 z-50 bg-white border-b border-stone-200 h-14 items-center px-6">
        <Link to="/feed" className="mr-6 flex-shrink-0 px-3 py-1">
          <img
            src={LOGO_SRC}
            alt="Neighborli"
            className="h-8 w-auto"
          />
        </Link>
        <div className="flex items-center gap-1 flex-1">
          {navLinks.map(({ path, icon: Icon, label }) => (
            <Link key={path} to={path} data-testid={`nav-${label.toLowerCase()}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium font-nunito transition-colors ${isActive(path) ? 'bg-green-50 text-[#004B00]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
              <Icon size={17} />
              {label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <NotificationDropdown />
          <div className="relative">
            <button onClick={() => setMenuOpen(!menuOpen)} data-testid="user-menu-btn"
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              {user?.profile_photo ? (
                <SmartImg
                  src={user.profile_photo}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover border-2 border-[#004B00]/20"
                  fallbackClassName={`w-8 h-8 rounded-full border-2 border-[#004B00]/20 ${uc.bg} ${uc.text} text-sm font-poppins font-semibold`}
                  fallback={initials(user)}
                />
              ) : (
                <div className={`w-8 h-8 rounded-full ${uc.bg} ${uc.text} flex items-center justify-center text-sm font-poppins font-semibold`}>
                  {initials(user)}
                </div>
              )}
              <span className="text-sm font-medium text-gray-700 font-nunito hidden lg:block">{user?.first_name || user?.name?.split(' ')[0]}</span>
              <ChevronDown size={14} className="text-gray-400 hidden lg:block" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-stone-100 py-2 z-50">
                {drawerLinks.map(link => (
                  <Link key={link.path} to={resolveHref(link)} onClick={() => setMenuOpen(false)} data-testid={link.testId}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 font-nunito">
                    <link.icon size={16} /> {link.label}
                  </Link>
                ))}
                <div className="border-t border-stone-100 my-1" />
                <button onClick={handleLogout} data-testid="logout-btn"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left font-nunito">
                  <LogOut size={16} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── Mobile Top Bar ──────────────────────────────────────────────
          Was missing entirely — that's why notifications / messages /
          user menu were unreachable on phones. Renders BELOW md breakpoint
          only, sticky so it stays visible on scroll. */}
      <nav className="md:hidden sticky top-0 left-0 right-0 z-50 bg-white border-b border-stone-200 h-14 flex items-center justify-between px-3">
        <Link to="/feed" className="flex items-center px-2 py-1" data-testid="mobile-logo">
          <img
            src={LOGO_SRC}
            alt="Neighborli" className="h-7 w-auto"
          />
        </Link>
        <div className="flex items-center gap-1">
          <NotificationDropdown />
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-50 transition-colors"
          >
            {user?.profile_photo ? (
              <SmartImg
                src={user.profile_photo}
                alt={user.name}
                className="w-8 h-8 rounded-full object-cover border-2 border-[#004B00]/20"
                fallbackClassName={`w-8 h-8 rounded-full border-2 border-[#004B00]/20 ${uc.bg} ${uc.text} text-sm font-poppins font-semibold`}
                fallback={initials(user) || <Menu size={16}/>}
              />
            ) : (
              <div className={`w-8 h-8 rounded-full ${uc.bg} ${uc.text} flex items-center justify-center text-sm font-poppins font-semibold`}>
                {initials(user) || <Menu size={16}/>}
              </div>
            )}
          </button>
        </div>
      </nav>

      {/* ── Mobile Slide-in Drawer ─────────────────────────────────────── */}
      {mobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-[60] bg-black/40 animate-in fade-in duration-150"
            onClick={closeMobileMenu}
            data-testid="mobile-menu-overlay"
          />
          <aside
            className="md:hidden fixed top-0 right-0 bottom-0 z-[70] w-[80%] max-w-xs bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
            data-testid="mobile-drawer"
          >
            {/* Drawer header — user identity + close */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <div className="flex items-center gap-3 min-w-0">
                {user?.profile_photo ? (
                  <SmartImg
                    src={user.profile_photo}
                    alt={user.name}
                    className="w-9 h-9 rounded-full object-cover"
                    fallbackClassName={`w-9 h-9 rounded-full ${uc.bg} ${uc.text} text-sm font-poppins font-semibold`}
                    fallback={initials(user)}
                  />
                ) : (
                  <div className={`w-9 h-9 rounded-full ${uc.bg} ${uc.text} flex items-center justify-center text-sm font-poppins font-semibold`}>
                    {initials(user)}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-poppins font-semibold text-gray-900 truncate">{user?.name}</div>
                  <div className="text-xs text-gray-400 font-nunito truncate">{user?.email}</div>
                </div>
              </div>
              <button
                onClick={closeMobileMenu}
                aria-label="Close menu"
                className="p-1.5 rounded-lg hover:bg-gray-50"
                data-testid="mobile-menu-close"
              >
                <X size={18} className="text-gray-500"/>
              </button>
            </div>

            {/* Drawer body — every destination reachable on mobile */}
            <div className="flex-1 overflow-y-auto py-2">
              {drawerLinks.map(link => (
                <Link
                  key={link.path}
                  to={resolveHref(link)}
                  onClick={closeMobileMenu}
                  data-testid={`mobile-${link.testId}`}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-nunito"
                >
                  <link.icon size={18} /> {link.label}
                </Link>
              ))}
              <div className="border-t border-stone-100 my-2" />
              <button
                onClick={() => { closeMobileMenu(); handleLogout(); }}
                data-testid="mobile-logout-btn"
                className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 font-nunito"
              >
                <LogOut size={18} /> Log out
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ── Mobile Bottom Tab Bar ─────────────────────────────────────── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-stone-200">
        <div className="grid grid-cols-5 h-16">
          {mobileNavLinks.slice(0, 5).map(({ path, icon: Icon, label }) => (
            <Link key={path} to={path}
              data-testid={`mobile-tab-${label.toLowerCase().replace(/[^a-z]/g,'')}`}
              className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-nunito ${isActive(path) ? 'text-[#004B00]' : 'text-gray-400'}`}>
              <Icon size={18} />
              {label.length > 7 ? label.slice(0, 6) + '.' : label}
            </Link>
          ))}
        </div>
      </nav>

      {menuOpen && <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />}
    </>
  );
}
